import React, { useRef } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { MultiSelectDropdown } from "components/shared/MultiSelectDropdown/MultiSelectDropdown";
import { EMPTY_QUESTION_FILTER, isNoOpFilter } from "./questionFilters";

// Re-export the pure filter helpers so existing importers of this module keep working.
export {
  EMPTY_QUESTION_FILTER,
  isNoOpFilter,
  toTrackQuestionFilters,
  customizedTrackTypes,
  serializeQuestionFilter,
  fromQuestionFilter,
} from "./questionFilters";

// The Advanced Selection filter row: Standards (multi, Core only) · Learning
// Components (multi) · Question Type (single) + the auto-assign-early-finishers
// checkbox. Facet-driven: dead options are disabled and live counts shown so the
// teacher can never reach an empty question set.
//
// Props:
//   filter        — { standards, learningComponents, questionType, autoAssignEarlyFinishers }
//   onChange      — (nextFilter) => void
//   facets        — { total, standards, learning_components, question_types }
//   showStandards — render the Standards multi-select (Core tab); false on prereq/ext
//   showAutoAssign— render the auto-assign checkbox (default true)
//   standardNames — optional { [code]: name } lookup; the facets response only carries
//                   codes, so callers with the name handy (e.g. a lesson's addressing
//                   set) can pass it through to show "CODE: Name" instead of a bare code.
//   hasLoaded/error— from useQuestionFacets: has a real response landed, and did the last
//                   one fail. Counts and dead-option disabling key off `hasLoaded && !error`
//                   and NOT the hook's `loading`, which is true on every interaction (so
//                   gating on it blinks the counts and greys out a control mid-click).
//                   Both default fail-open, so a caller that hasn't been wired up yet
//                   behaves exactly as before rather than sitting in a dead "no data" state.
export const QuestionFilterPanel = ({
  filter = EMPTY_QUESTION_FILTER,
  onChange,
  facets,
  showStandards = true,
  showAutoAssign = true,
  standardNames = {},
  hasLoaded = true,
  error = false,
  // Optional node rendered at the right end of the filter row (e.g. the
  // "Create My Question Set" CTA in the preview modal).
  trailingAction = null,
  // Standard code shown as static text when the whole panel is scoped to a single
  // standard (`showStandards` false): there is no choice to make, but the teacher
  // should still see which standard the questions belong to.
  staticStandardCode = null,
}) => {
  const checkboxIdRef = useRef(null);
  if (checkboxIdRef.current === null) checkboxIdRef.current = `auto-assign-${Math.round(performance.now())}-${Math.floor(Math.random() * 1e6)}`;

  const set = (patch) => onChange({ ...filter, ...patch });
  // Reset CTA only shows once the teacher has actually narrowed something.
  const showReset = !isNoOpFilter(filter);
  // Any selection that shrinks the question pool (auto-assign doesn't).
  const poolNarrowed = Boolean(
    filter.standards?.length ||
      filter.learningComponents?.length ||
      (filter.questionType && filter.questionType !== "both"),
  );
  // "The counts on screen are real" — the only condition under which a zero may be shown
  // as a zero or used to disable an option.
  const ready = hasLoaded && !error;

  const standardOptions = (facets?.standards || []).map((s) => {
    const code = (s.code || "").toUpperCase();
    const name = standardNames?.[s.code];
    return {
      value: s.code,
      label: name ? `${code}: ${name}` : code,
      count: s.count,
      disabled: s.count === 0,
    };
  });

  const lcOptions = (facets?.learning_components || []).map((lc) => ({
    value: lc.lc_id,
    label: lc.description || lc.lc_id,
    count: lc.count,
    disabled: lc.count === 0,
  }));

  // Question Type is a fixed enum, so both options always render — they must not
  // wait on the response the way facet-derived options do. Counts annotate them and dead
  // ones disable, but only once real facets have arrived: before that every count is a
  // placeholder zero, not an answer.
  const qt = facets?.question_types || {};
  const mcCount = qt.multiple_choice ?? 0;
  const orCount = qt.open_response ?? 0;
  const typeOption = (value, name, count) => ({
    value,
    label: name,
    ...(ready && { count }),
    // Never disable the current selection — the teacher must be able to switch away from
    // a type that has since gone empty.
    disabled: ready && count === 0 && filter.questionType !== value,
  });
  const questionTypeOptions = [
    typeOption("multiple_choice", "Multiple Choice", mcCount),
    typeOption("open_response", "Open Response", orCount),
  ];
  // The enum rides the multi-select: nothing checked === "both" (no filter), one box
  // checked === that type, and ticking the second collapses back to "both" — both
  // types together IS the unfiltered state, and the enum has no list form.
  const questionTypeValues =
    filter.questionType === "multiple_choice" || filter.questionType === "open_response"
      ? [filter.questionType]
      : [];

  return (
    <Panel>
      <FilterRow>
        {!showStandards && staticStandardCode && (
          <StaticField>
            <StaticFieldLabel>Standard</StaticFieldLabel>
            <StaticFieldValue>{String(staticStandardCode).toUpperCase()}</StaticFieldValue>
          </StaticField>
        )}
        {showStandards && (
          <FilterCell>
            {standardOptions.length === 1 && !poolNarrowed ? (
              // One available standard leaves nothing to choose, so show it as static
              // text instead of a one-option dropdown — but only while NO narrowing is
              // active. The facets drop standards with zero questions under the current
              // LC / type selection, so a multi-standard scope can shrink to one entry
              // mid-filter; the dropdown must stay so the teacher can widen again. A
              // persisted standards selection likewise keeps its control, or it would
              // stay silently applied with nothing to see or clear it.
              <StaticField>
                <StaticFieldLabel>Standard</StaticFieldLabel>
                <StaticFieldValue>{String(standardOptions[0].value || "").toUpperCase()}</StaticFieldValue>
              </StaticField>
            ) : (
              <MultiSelectDropdown
                label="Standards"
                allNoun="Standards"
                values={filter.standards || []}
                options={standardOptions}
                onChange={(standards) => set({ standards })}
              />
            )}
          </FilterCell>
        )}
        {/* Keep the cell mounted until facets land, so it can't materialise mid-reach and
            shove Question Type sideways. Disabled only while there is genuinely nothing to
            pick — a failed load leaves it clickable so it shows "No options available."
            rather than a dead grey box, and refetches never disable it. */}
        {(!hasLoaded || lcOptions.length > 0) && (
          <FilterCell>
            <MultiSelectDropdown
              label="Learning Components"
              allNoun="Learning Components"
              values={filter.learningComponents || []}
              options={lcOptions}
              onChange={(learningComponents) => set({ learningComponents })}
              disabled={!hasLoaded && !error}
            />
          </FilterCell>
        )}
        <FilterCell>
          <MultiSelectDropdown
            label="Question Type"
            allNoun="Question Types"
            values={questionTypeValues}
            options={questionTypeOptions}
            onChange={(next) => set({ questionType: next.length === 1 ? next[0] : "both" })}
          />
        </FilterCell>
        {showReset && (
          <ResetFiltersButton type="button" onClick={() => onChange(EMPTY_QUESTION_FILTER)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M21 12C21 13.78 20.4722 15.5201 19.4832 17.0001C18.4943 18.4802 17.0887 19.6337 15.4442 20.3149C13.7996 20.9961 11.99 21.1743 10.2442 20.8271C8.49836 20.4798 6.89472 19.6226 5.63604 18.364C4.37737 17.1053 3.5202 15.5016 3.17294 13.7558C2.82567 12.01 3.0039 10.2004 3.68509 8.55585C4.36628 6.91131 5.51983 5.50571 6.99987 4.51677C8.47991 3.52784 10.22 3 12 3C14.52 3 16.93 4 18.74 5.74L21 8M16 8H21L21 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Reset
          </ResetFiltersButton>
        )}
        {trailingAction && <TrailingCell>{trailingAction}</TrailingCell>}
      </FilterRow>

      {showAutoAssign && (
        <AutoAssignCheckbox
          checked={!!filter.autoAssignEarlyFinishers}
          onChange={(autoAssignEarlyFinishers) => set({ autoAssignEarlyFinishers })}
          inputId={checkboxIdRef.current}
        />
      )}
    </Panel>
  );
};

// The auto-assign block on its own, so views that show it without the filter row
// (the Question Set page's My Question Set tab) reuse the same control.
export const AutoAssignCheckbox = ({ checked, onChange, inputId }) => {
  const fallbackIdRef = useRef(null);
  if (fallbackIdRef.current === null) fallbackIdRef.current = `auto-assign-${Math.round(performance.now())}-${Math.floor(Math.random() * 1e6)}`;
  const id = inputId || fallbackIdRef.current;

  return (
    <AutoAssignGroup>
      <AutoAssignTitle>Auto-assign for early finishers</AutoAssignTitle>
      <CheckboxRow>
        <HiddenCheckbox
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <CheckboxLabel htmlFor={id}>
          <Box $checked={checked} aria-hidden>
            {checked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: "11px", color: "white" }} />}
          </Box>
          <span>
            Keep groups on task with additional questions if they complete the set before time is up.
          </span>
        </CheckboxLabel>
      </CheckboxRow>
    </AutoAssignGroup>
  );
};

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 8px 0 16px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

// Each dropdown lives in a cell capped at 300px. The dropdown's own container is
// flex:1 inside this cell, so it fills the cell; overflowing trigger text
// ellipsis-truncates (the triggers have overflow:hidden + text-overflow:ellipsis and
// min-width:0). Grows to fill spare row width, but never past 300px.
const FilterCell = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  max-width: 300px;
`;

// The single-standard static replacement for the Standards dropdown: same label
// styling as the dropdowns' FieldLabel, value aligned with the 40px triggers.
const StaticField = styled.div`
  display: flex;
  flex-direction: column;
`;

const StaticFieldLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #002e19;
  margin-bottom: 6px;
  white-space: nowrap;
`;

const StaticFieldValue = styled.span`
  display: flex;
  align-items: center;
  min-height: 40px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #002e19;
`;

// Sits at the end of the row, aligned with the dropdown triggers (not their labels)
// via align-self: flex-end + a matching 40px min-height.
const ResetFiltersButton = styled.button`
  align-self: flex-end;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 4px;
  background: transparent;
  border: none;
  color: #524fc9;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
  text-align: center;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
  &:focus-visible {
    outline: 2px solid #605dec;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

// Pins the trailing action to the row's right edge, aligned with the dropdown
// triggers like the reset button above.
const TrailingCell = styled.div`
  align-self: flex-end;
  flex-shrink: 0;
  margin-left: auto;
`;

// Groups the auto-assign title + checkbox row at a tight 4px gap — its own flex item
// within Panel, so Panel's larger gap still separates it from the filter row above.
const AutoAssignGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Bold header above the auto-assign checkbox.
const AutoAssignTitle = styled.p`
  margin: 0;
  color: #002e19;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

// Visually hidden but focusable native checkbox drives the state + a11y; the styled
// Box is the visual, and clicking the label toggles the input.
const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #002e19;
`;

const Box = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 4px;
  border: 1px solid ${(p) => (p.$checked ? "#00A35A" : "#ACAFAD")};
  background: ${(p) => (p.$checked ? "#00A35A" : "white")};

  ${HiddenCheckbox}:focus-visible + label & {
    outline: 2px solid #00a35a;
    outline-offset: 1px;
  }
`;
